import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CalendarRange, ArrowLeft, Search, FileSpreadsheet, Printer, DollarSign, TrendingUp, TrendingDown } from 'lucide-react';
import Layout from '../../../components/Layout';
import { inventoryReportService, OpeningClosingStockDTO } from '../../../services/inventoryReportService';
import { exportToCsv, printReport } from '../../../utils/exportUtils';
import './InventoryReportCommon.css';

const fmt    = (n: number) => new Intl.NumberFormat(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
const fmtQty = (n: number) => new Intl.NumberFormat().format(n);
const today   = () => new Date().toISOString().split('T')[0];
const monthAgo = () => { const d = new Date(); d.setMonth(d.getMonth() - 1); return d.toISOString().split('T')[0]; };

const OpeningClosingStockReportPage: React.FC = () => {
  const navigate  = useNavigate();
  const [startDate,   setStartDate]   = useState(monthAgo());
  const [endDate,     setEndDate]     = useState(today());
  const [hasSearched, setHasSearched] = useState(false);
  const [searching,   setSearching]   = useState(false);
  const [data,        setData]        = useState<OpeningClosingStockDTO | null>(null);

  const handleSearch = async () => {
    setSearching(true);
    setHasSearched(true);
    try {
      const res = await inventoryReportService.getOpeningClosing({ startDate, endDate });
      const d: OpeningClosingStockDTO = (res.data as any)?.data ?? res.data;
      setData(d);
    } catch { setData(null); }
    finally { setSearching(false); }
  };

  const handleExcel = () => {
    if (!data) return;
    exportToCsv(
      `opening-closing-stock-${startDate}_${endDate}.csv`,
      ['SKU', 'Product', 'Category', 'Opening Qty', 'IN', 'OUT', 'Adjustment', 'Closing Qty', 'Unit Cost', 'Opening Value', 'Closing Value'],
      data.items.map(i => [
        i.sku, i.productName, i.categoryName ?? '',
        i.openingQuantity, i.inQuantity, i.outQuantity, i.adjustmentQuantity, i.closingQuantity,
        i.unitCost, i.openingValue, i.closingValue,
      ]),
    );
  };

  const items = data?.items ?? [];

  return (
    <Layout>
      <div className="irp-page">

        <div className="irp-hero">
          <div>
            <h1 className="irp-hero-title"><CalendarRange size={22} strokeWidth={1.8} /> Opening vs Closing Stock</h1>
            <p className="irp-hero-sub">Period-based opening balance, movements (IN/OUT), and closing balance with values</p>
          </div>
          <div className="irp-hero-actions">
            {hasSearched && items.length > 0 && (
              <div className="irp-export-group">
                <button className="irp-export-btn irp-export-excel" onClick={handleExcel}><FileSpreadsheet size={13} /> Export Excel</button>
                <button className="irp-export-btn irp-export-pdf" onClick={printReport}><Printer size={13} /> Print / PDF</button>
              </div>
            )}
            <button className="irp-back-btn" onClick={() => navigate('/reports/inventory')}><ArrowLeft size={14} /> Back</button>
          </div>
        </div>

        <div className="ims-card">
          <div className="ims-card-header">
            <span className="ims-card-title"><Search size={14} style={{ verticalAlign: 'middle', marginInlineEnd: 6 }} />Period</span>
          </div>
          <div className="ims-card-body" style={{ padding: '1rem 1.25rem' }}>
            <div className="irp-filter-bar">
              <div className="irp-filter-group">
                <span className="irp-filter-label">Period Start</span>
                <input type="date" className="ims-form-control" value={startDate} onChange={e => setStartDate(e.target.value)} />
              </div>
              <div className="irp-filter-group">
                <span className="irp-filter-label">Period End</span>
                <input type="date" className="ims-form-control" value={endDate} onChange={e => setEndDate(e.target.value)} />
              </div>
              <button className="ims-btn ims-btn-primary" onClick={handleSearch} disabled={searching} style={{ alignSelf: 'flex-end' }}>
                {searching ? <><span className="irp-spinner" /> Searching…</> : <><Search size={14} /> Generate</>}
              </button>
            </div>
          </div>
        </div>

        {!hasSearched && (
          <div className="irp-prompt">
            <CalendarRange size={48} strokeWidth={1} />
            <p>Select a period and click <strong>Generate</strong> to view opening and closing stock.</p>
          </div>
        )}

        {hasSearched && !searching && data && (
          <>
            <div className="ims-stats-row" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))' }}>
              <div className="ims-stat-card">
                <div className="ims-stat-icon irp-icon-total"><DollarSign size={20} strokeWidth={1.6} /></div>
                <div className="ims-stat-info">
                  <span className="ims-stat-value" style={{ fontSize: '1.05rem' }}>{fmt(data.openingValue)}</span>
                  <span className="ims-stat-label">Opening Value</span>
                </div>
              </div>
              <div className="ims-stat-card">
                <div className="ims-stat-icon irp-icon-value"><DollarSign size={20} strokeWidth={1.6} /></div>
                <div className="ims-stat-info">
                  <span className="ims-stat-value" style={{ fontSize: '1.05rem' }}>{fmt(data.closingValue)}</span>
                  <span className="ims-stat-label">Closing Value</span>
                </div>
              </div>
              <div className="ims-stat-card">
                <div className="ims-stat-icon irp-icon-in"><TrendingUp size={20} strokeWidth={1.6} /></div>
                <div className="ims-stat-info">
                  <span className="ims-stat-value irp-positive">+{fmtQty(data.totalInQty)}</span>
                  <span className="ims-stat-label">Total IN (qty)</span>
                </div>
              </div>
              <div className="ims-stat-card">
                <div className="ims-stat-icon irp-icon-out"><TrendingDown size={20} strokeWidth={1.6} /></div>
                <div className="ims-stat-info">
                  <span className="ims-stat-value irp-negative">-{fmtQty(data.totalOutQty)}</span>
                  <span className="ims-stat-label">Total OUT (qty)</span>
                </div>
              </div>
            </div>

            {items.length === 0 ? (
              <div className="ims-card"><div className="irp-empty">No stock data found for the selected period.</div></div>
            ) : (
              <div className="ims-card">
                <div className="ims-card-header">
                  <span className="ims-card-title">
                    <CalendarRange size={14} style={{ verticalAlign: 'middle', marginInlineEnd: 6 }} />
                    Opening vs Closing — {data.periodStart} to {data.periodEnd} ({items.length} products)
                  </span>
                </div>
                <div className="ims-card-body" style={{ padding: 0, overflowX: 'auto' }}>
                  <table className="irp-table">
                    <thead>
                      <tr>
                        <th>SKU</th>
                        <th>Product</th>
                        <th>Category</th>
                        <th className="num">Opening Qty</th>
                        <th className="num">IN</th>
                        <th className="num">OUT</th>
                        <th className="num">Adj.</th>
                        <th className="num">Closing Qty</th>
                        <th className="num">Unit Cost</th>
                        <th className="num">Opening Value</th>
                        <th className="num">Closing Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map(item => {
                        const valueDiff = item.closingValue - item.openingValue;
                        return (
                          <tr key={item.productId}>
                            <td><span className="irp-sku">{item.sku}</span></td>
                            <td style={{ fontWeight: 500 }}>{item.productName}</td>
                            <td>{item.categoryName ?? '—'}</td>
                            <td className="num">{fmtQty(item.openingQuantity)}</td>
                            <td className="num irp-positive">+{fmtQty(item.inQuantity)}</td>
                            <td className="num irp-negative">-{fmtQty(item.outQuantity)}</td>
                            <td className={`num ${item.adjustmentQuantity !== 0 ? (item.adjustmentQuantity > 0 ? 'irp-positive' : 'irp-negative') : ''}`}>
                              {item.adjustmentQuantity !== 0
                                ? (item.adjustmentQuantity > 0 ? `+${fmtQty(item.adjustmentQuantity)}` : fmtQty(item.adjustmentQuantity))
                                : '—'}
                            </td>
                            <td className="num" style={{ fontWeight: 600 }}>{fmtQty(item.closingQuantity)}</td>
                            <td className="num">{fmt(item.unitCost)}</td>
                            <td className="num">{fmt(item.openingValue)}</td>
                            <td className={`num ${valueDiff > 0 ? 'irp-positive' : valueDiff < 0 ? 'irp-negative' : 'irp-value'}`}>
                              {fmt(item.closingValue)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td colSpan={3} style={{ fontWeight: 700 }}>TOTAL</td>
                        <td className="num">{fmtQty(items.reduce((s, i) => s + i.openingQuantity, 0))}</td>
                        <td className="num">{fmtQty(data.totalInQty)}</td>
                        <td className="num">{fmtQty(data.totalOutQty)}</td>
                        <td className="num">{fmtQty(data.totalAdjustmentQty)}</td>
                        <td className="num">{fmtQty(items.reduce((s, i) => s + i.closingQuantity, 0))}</td>
                        <td></td>
                        <td className="num">{fmt(data.openingValue)}</td>
                        <td className="num">{fmt(data.closingValue)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            )}

            {/* Breakdown */}
            {items.length > 0 && (
              <>
                <span className="irp-section-title">Period Summary</span>
                <div className="irp-breakdown-grid">
                  <div className="irp-breakdown-card irp-breakdown-indigo">
                    <div className="irp-breakdown-label">Opening Value</div>
                    <div className="irp-breakdown-value" style={{ fontSize: '1.4rem' }}>{fmt(data.openingValue)}</div>
                    <div className="irp-breakdown-sub">start of period</div>
                  </div>
                  <div className="irp-breakdown-card irp-breakdown-green">
                    <div className="irp-breakdown-label">Closing Value</div>
                    <div className="irp-breakdown-value" style={{ fontSize: '1.4rem' }}>{fmt(data.closingValue)}</div>
                    <div className="irp-breakdown-sub">end of period</div>
                  </div>
                  <div className="irp-breakdown-card irp-breakdown-amber">
                    <div className="irp-breakdown-label">Value Change</div>
                    <div className={`irp-breakdown-value ${data.closingValue >= data.openingValue ? 'irp-positive' : 'irp-negative'}`} style={{ fontSize: '1.4rem' }}>
                      {data.closingValue >= data.openingValue ? '+' : ''}{fmt(data.closingValue - data.openingValue)}
                    </div>
                    <div className="irp-breakdown-sub">net movement</div>
                  </div>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </Layout>
  );
};

export default OpeningClosingStockReportPage;
